import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Linkedin, Instagram } from 'lucide-react';
import axios from 'axios';

const PublicLayout = ({ children }) => {
  const { user } = useAuth();
  const [social, setSocial] = useState({});

  useEffect(() => {
    axios.get(`${API}/settings/public`).then(res => {
      setSocial({ linkedin: res.data?.social_linkedin || '', instagram: res.data?.social_instagram || '' });
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/edassist-logo.png" alt="EdAssist" className="h-8 w-auto object-contain" />
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/jobs" className="text-slate-600 hover:text-slate-900 transition-colors">
                Browse Jobs
              </Link>
              <Link to="/resume-builder" className="text-slate-600 hover:text-slate-900 transition-colors">
                Resume Builder
              </Link>
              <Link to="/training" className="text-slate-600 hover:text-slate-900 transition-colors">
                Training
              </Link>
              <Link to="/blogs" className="text-slate-600 hover:text-slate-900 transition-colors">
                Blog
              </Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900 transition-colors">
                Pricing
              </Link>
            </nav>

            <div className="flex items-center gap-3">
              {user ? (
                <Link to="/dashboard">
                  <Button data-testid="dashboard-btn">Dashboard</Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" data-testid="login-btn">Log in</Button>
                  </Link>
                  <Link to="/register">
                    <Button data-testid="get-started-btn">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/edassist-logo.png" alt="EdAssist" className="h-8 w-auto object-contain brightness-0 invert" />
              </div>
              <p className="text-sm leading-relaxed">India's trusted job board for educators, teachers, and academic professionals.</p>
              <p className="text-xs text-slate-500 mt-2">EdAssist.co.in</p>
              {(social.linkedin || social.instagram) && (
                <div className="flex items-center gap-3 mt-4">
                  {social.linkedin && (
                    <a href={social.linkedin} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-indigo-600 transition-colors" data-testid="footer-linkedin">
                      <Linkedin className="w-4 h-4 text-white" />
                    </a>
                  )}
                  {social.instagram && (
                    <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-pink-600 transition-colors" data-testid="footer-instagram">
                      <Instagram className="w-4 h-4 text-white" />
                    </a>
                  )}
                </div>
              )}
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/jobs" className="hover:text-white transition-colors">Browse Jobs</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/training" className="hover:text-white transition-colors">Training Programs</Link></li>
                <li><Link to="/resume-builder" className="hover:text-white transition-colors">Resume Builder</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/blogs" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/ambassador" className="hover:text-white transition-colors">Campus Ambassador</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link></li>
                <li><Link to="/fraud-alert" className="hover:text-white transition-colors text-red-400 hover:text-red-300">Fraud Alert</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 text-center text-sm">
            &copy; {new Date().getFullYear()} EdAssist (HRassistance India Consultancy LLP). All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
